import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { KeyRound, CheckCircle2 } from 'lucide-react';
import { Isotipo } from '../components/shared/Logo';
import ThemeToggle from '../components/layout/ThemeToggle';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/auth.service';

/**
 * Destino del enlace de recuperación (S-14). Supabase abre la app con una sesión de
 * recuperación; aquí el usuario fija la contraseña nueva con updateUser.
 */
export default function RestablecerContrasena() {
  const { session, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    setSaving(true);
    try {
      const { error: updateError } = await authService.updatePassword(password);
      if (updateError) throw updateError;
      setDone(true);
    } catch (err: any) {
      setError(err.message || 'No se pudo actualizar la contraseña.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent';
  const primaryButton =
    'w-full bg-blue-600 text-white py-2 px-4 rounded-md font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

  let body: JSX.Element;
  if (authLoading) {
    body = <p className="text-center text-sm text-gray-600 py-4">Cargando...</p>;
  } else if (done) {
    body = (
      <div className="text-center py-2">
        <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-6 h-6 text-green-600" />
        </div>
        <p className="text-gray-900 font-medium mb-6">Contraseña actualizada</p>
        <button type="button" onClick={() => navigate('/chat')} className={primaryButton}>
          Ir a AlertAI
        </button>
      </div>
    );
  } else if (!session) {
    body = (
      <div className="text-center py-2">
        <p className="text-gray-900 font-medium mb-2">El enlace no es válido o ya expiró</p>
        <p className="text-gray-600 text-sm mb-6">Pide uno nuevo desde «¿Olvidaste tu contraseña?».</p>
        <Link to="/login" className="text-sm text-blue-600 hover:text-blue-700 font-medium">
          Volver a iniciar sesión
        </Link>
      </div>
    );
  } else {
    body = (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center gap-2 text-gray-900 font-medium">
          <KeyRound className="w-4 h-4 text-gray-500" />
          Crea una contraseña nueva
        </div>
        <div>
          <label htmlFor="new-password" className="block text-sm font-medium text-gray-700 mb-1">
            Contraseña nueva
          </label>
          <input
            id="new-password"
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
            Repite la contraseña
          </label>
          <input
            id="confirm-password"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            className={inputClass}
          />
        </div>
        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">{error}</div>
        )}
        <button type="submit" disabled={saving} className={primaryButton}>
          {saving ? 'Guardando...' : 'Guardar contraseña'}
        </button>
      </form>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <ThemeToggle className="fixed top-4 right-4" />
      <div className="w-full max-w-md">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="flex flex-col items-center mb-8">
            <Isotipo className="w-14 h-14 mb-3" />
            <h1 aria-label="AlertAI" className="text-2xl font-bold tracking-tight text-brand-navy">
              Alert<span className="text-brand-blue">AI</span>
            </h1>
            <p className="text-gray-600 text-sm mt-1">Asistente tributario</p>
          </div>
          {body}
        </div>
      </div>
    </div>
  );
}
